<template>
  <!-- V2: polished Tailwind/Seranova-aligned card -->
  <div
    v-if="customer && isV2"
    class="payment-methods"
    data-cy="saved-payment-methods"
  >
    <div
      v-for="method in paymentMethods"
      :key="method.id"
      class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300"
      :class="{
        'border-blue-500 ring-1 ring-blue-500': method.id === selectedID
      }"
      @click="selectPaymentMethod(null)"
    >
      <div v-if="method.payment_type === 'card'" class="flex-1 min-w-0">
        <div class="flex items-center gap-2 flex-wrap">
          <span :class="brandPillClasses(method.brand)">{{
            brandLabel(method.brand)
          }}</span>
          <span
            class="font-mono text-sm tabular-nums tracking-wide text-gray-900 font-medium"
          >
            •••• {{ method.last4 }}
          </span>
          <span class="text-xs text-gray-500">
            Expires
            <span class="font-mono tabular-nums">
              {{ String(method.exp_month).padStart(2, "0") }}/{{
                method.exp_year
              }}
            </span>
          </span>
          <span
            v-if="method.expired"
            class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-900"
          >
            Expired
          </span>
        </div>
        <div
          v-if="savedPaymentBillingAddress?.cityStateZip"
          class="mt-2 text-xs text-gray-600 leading-snug"
        >
          <div class="font-medium text-gray-700">Billing address</div>
          <div>{{ savedPaymentBillingAddress.name }}</div>
          <div>{{ savedPaymentBillingAddress.street }}</div>
          <div>{{ savedPaymentBillingAddress.cityStateZip }}</div>
        </div>
        <div v-else class="mt-2 text-xs text-gray-600">
          {{ fullName }}
        </div>
      </div>
      <div
        v-if="method.payment_type === 'ach'"
        class="flex-1 min-w-0 flex items-center gap-2 text-sm text-gray-900"
      >
        <i
          class="fa-regular fa-building-columns text-gray-600"
          aria-hidden="true"
        ></i>
        <span>
          {{ capitalize(method.ach_account_type) }} account ending in
          <strong class="font-mono tabular-nums font-medium">{{
            method.last4
          }}</strong>
        </span>
      </div>
      <button
        v-if="hasDelete"
        type="button"
        class="text-gray-400 hover:text-red-600 transition-colors p-1 -m-1 rounded"
        :aria-label="`Remove payment method ending in ${method.last4}`"
        data-cy="delete-payment-method"
        @click.stop="handleDeletePaymentMethod(method)"
      >
        <i class="fa-regular fa-trash-can" aria-hidden="true"></i>
      </button>
    </div>
    <div
      v-if="!paymentMethods.length"
      class="text-sm text-gray-500 text-center py-3"
    >
      No payment method on file
    </div>
  </div>

  <!-- V1: original markup, unchanged -->
  <div v-else-if="customer" class="payment-methods">
    <div
      v-for="method in paymentMethods"
      :key="method.id"
      class="d-flex justify-content-between align-items-center payment-method"
      :class="{ active: method.id === selectedID }"
      @click="selectPaymentMethod(null)"
    >
      <div v-if="method.payment_type === 'card'">
        <div class="d-flex align-items-center">
          <component :is="cardImages[method.brand]" class="mr-2" />
          <span>
            <b>{{ method.brand }}</b> ending in
            <b>{{ method.last4 }}</b> expiring on
            <b>{{ method.exp_month }}/{{ method.exp_year }}</b>
          </span>
          <span v-if="method.expired" class="ml-1"><b>(Expired)</b></span>
        </div>
        <div>
          <!-- Support existing payment methods without billing address info -->
          <div v-if="savedPaymentBillingAddress?.cityStateZip">
            <div><b>Billing Address:</b></div>
            <div>{{ savedPaymentBillingAddress.name }}</div>
            <div>{{ savedPaymentBillingAddress.street }}</div>
            <div>{{ savedPaymentBillingAddress.cityStateZip }}</div>
          </div>
          <div v-else>
            <div>{{ fullName }}</div>
          </div>
        </div>
      </div>
      <div
        v-if="method.payment_type === 'ach'"
        class="d-flex align-items-center"
      >
        <i class="fas fa-university mr-2"></i>
        <span>
          {{ capitalize(method.ach_account_type) }} account ending in
          <b>{{ method.last4 }}</b>
        </span>
      </div>
      <i
        v-if="hasDelete"
        class="fas fa-trash-alt link"
        @click="handleDeletePaymentMethod(method)"
      />
    </div>
    <div v-if="!paymentMethods.length" class="no-results-message p-0">
      No payment method on file
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { EventBus } from "@/lib/event_bus.js";

import amexSrc from "@/images/amex.svg?component";
import discoverSrc from "@/images/discover.svg?component";
import mastercardSrc from "@/images/mastercard.svg?component";
import visaSrc from "@/images/visa.svg?component";
import usePaymentMethods from "./usePaymentMethods";

export default {
  name: "CustomerPaymentMethods",
  props: {
    customer: {
      type: Object,
      default: null
    },
    selectedID: {
      type: Number,
      default: null
    },
    selectPaymentMethod: {
      type: Function,
      default: () => {}
    },
    onPaymentMethodRemoved: {
      type: Function
    },
    // V1 callers omit and get the original "default" markup. V2 (via
    // PaymentMethods chromeless mode) cascades "v2" so the saved-card row
    // can render polished spacing/typography while V1 stays untouched.
    variant: {
      type: String,
      default: "default",
      validator: (v) => ["default", "v2"].includes(v)
    }
  },
  emits: ["payment-method-removed"],
  setup() {
    const { savedPaymentBillingAddress, deletePaymentMethod, fullName } =
      usePaymentMethods();
    return {
      fullName,
      savedPaymentBillingAddress,
      deletePaymentMethod
    };
  },
  data() {
    return {
      cardImages: {
        Visa: visaSrc,
        MasterCard: mastercardSrc,
        Discover: discoverSrc,
        "American Express": amexSrc
      },
      open: false,
      deleteId: null
    };
  },
  computed: {
    hasDelete() {
      return this.$props.onPaymentMethodRemoved;
    },
    paymentMethods() {
      return this.customer.payment_method ? [this.customer.payment_method] : [];
    },
    isV2() {
      return this.variant === "v2";
    }
  },
  methods: {
    ...mapActions(["confirmDelete"]),
    brandLabel(brand) {
      const b = (brand || "").toLowerCase();
      if (b.includes("master")) return "MC";
      if (b.includes("amex") || b.includes("american")) return "AMEX";
      if (b.includes("visa")) return "VISA";
      if (b.includes("discover")) return "DISC";
      return (brand || "CARD").toUpperCase();
    },
    brandPillClasses(brand) {
      const base =
        "inline-flex items-center font-mono text-[10px] font-semibold tracking-wider text-white px-1.5 py-0.5 rounded";
      const b = this.brandLabel(brand);
      if (b === "MC") return `${base} bg-red-600`;
      if (b === "AMEX") return `${base} bg-blue-700`;
      if (b === "VISA") return `${base} bg-gray-900`;
      if (b === "DISC") return `${base} bg-orange-500`;
      return `${base} bg-gray-700`;
    },
    handleDeletePaymentMethod(paymentMethod) {
      this.confirmDelete({
        message:
          "This action cannot be undone. Are you sure you wish to continue?",
        title: "Confirm Remove Payment Method"
      }).then(() => {
        this.deletePaymentMethod(paymentMethod.payment_method_delete_url).then(
          () => {
            // for parent component
            this.$emit("payment-method-removed");

            // for any other components
            EventBus.$emit("payment-method-removed");
          }
        );
      });
    }
  }
};
</script>
